import { useQueryClient, useJobStatus } from 'shared-ui';
import { toast } from 'react-toastify';

import { deleteMultiOwners } from 'api/owners/owners';
import { JobIdResponse } from 'api/jobs/jobs.types';
import { QueryKeyEnum } from 'core/global.enum';
import { useLocale } from 'hooks/useLocale/useLocale';
import { AppMessages } from 'i18n/messages';
import { useSelectedOwner } from 'hooks/useSelectedOwner/useSelectedOwner';
import { useMutation } from 'hooks/useMutation/useMutation';
import { useModalState } from 'hooks/useModalState/useModalState';

import { DeleteMultiOwners } from './DeleteMultiOwners';
import { DeleteMultiOwnersContainerProps } from './DeleteMultiOwners.types';

export const DeleteMultiOwnersContainer = ({ checkedOwners, setCheckedOwners }: DeleteMultiOwnersContainerProps) => {
  const queryClient = useQueryClient();
  const { formatMessage } = useLocale();
  const { selectedOwner, setSelectedOwner } = useSelectedOwner();
  const { isModalOpen, onModalOpen, onModalClose } = useModalState();

  const handleJobComplete = () => {
    if (selectedOwner && checkedOwners.some(({ id }) => id === selectedOwner?.id)) {
      setSelectedOwner(undefined);
    }
    queryClient.invalidateQueries([QueryKeyEnum.OWNERS]);
    toast.dark(formatMessage({ id: AppMessages['owners.multiAction.delete.success'] }));
    setCheckedOwners([]);
    onModalClose();
  };

  const handleJobError = (error: string) => {
    toast.error(formatMessage({ id: AppMessages['common.error'] }) || `Error: ${error}`);
  };

  const jobStatus = useJobStatus({
    useGlobalModal: true,
    autoClose: false,
    onComplete: handleJobComplete,
    onError: handleJobError,
    showItemCount: true,
    customCompleteMessage: formatMessage({ id: AppMessages['owners.batchDelete.result.success'] }),
    customFailedMessage: formatMessage({ id: AppMessages['owners.batchDelete.result.failed'] }),
    customActiveMessage: formatMessage({ id: AppMessages['owners.batchDelete.result.processing'] }),
    title: formatMessage({ id: AppMessages['owners.batchDelete.progressTitle'] }),
  });

  const { mutate: onDeleteMultiOwners, isLoading } = useMutation(deleteMultiOwners, {
    onSuccess: (response: JobIdResponse) => {
      if (!response || !response.jobId) {
        toast.error(formatMessage({ id: AppMessages['common.error'] }));
        return;
      }
      jobStatus.startJob(response.jobId);
      onModalClose();
    },
    onError: (error: unknown) => {
      const errorObj = error as Record<string, unknown>;
      toast.error(
        formatMessage({ id: AppMessages['common.error'] }) || `Error: ${errorObj?.message || 'Unknown error'}`,
      );
    },
  });

  const onSubmit = () => {
    onDeleteMultiOwners({ body: checkedOwners.map(({ id }) => id) });
  };

  return (
    <DeleteMultiOwners
      checkedOwners={checkedOwners}
      isModalOpen={isModalOpen}
      onModalClose={onModalClose}
      onModalOpen={onModalOpen}
      isLoading={isLoading}
      onSubmit={onSubmit}
    />
  );
};
